<template>
    <modal :title="title" :id="id" size="lg" :actionButtons="false" :closeButton="true" @shown="shown">
        <div class="row">
            <div class="col-6">
                <h4>{{$t('Invoiced from')}}</h4>
            </div>
            <div class="col-6 text-right pr-5">
                <div class="text-muted mb-4" v-if="user">
                    <strong class="text-body">{{user.firstName}} {{user.lastName}}</strong>
                </div>
            </div>
            <div class="col-6">
                <h4>{{$t('Invoiced to')}}</h4>
            </div>
            <div class="col-6 text-right pr-5">
                <div class="text-muted mb-4" v-if="parent.customer">
                    <strong class="text-body">{{parent.customer.first_name}} {{parent.customer.last_name}}</strong>
                    <div>{{parent.customer.phone}}</div>
                    <div>{{parent.customer.email}}</div>
                </div>
            </div>
            <div class="col-6" v-if="invoice">
                <h4>{{$t('Total paid')}}</h4>
            </div>
            <div class="col text-right pr-5" v-if="invoice">
                <span class="h3">
                    <money :value="parent.paidTotal"></money>
                </span>
            </div>
            <div class="col-6" v-if="invoice">
                <h4>{{$t('Total amount due')}}</h4>
            </div>
            <div class="col text-right pr-5 mt-2" v-if="invoice">
                <span class="h3">
                    <money :value="invoice.totalPrice"></money>
                </span>
            </div>
            <div class="col-12 mt-4">
                <div class="card" data-toggle="lists">
                    <div class="card-header">
                        <div class="row align-items-center">
                            <div class="col"></div>
                            <div class="col-auto" v-if="invoice && !parent.cancelled && (parent.paidTotal < invoice.totalPrice)">
                                <b-button
                                    variant="info"
                                    @click="openTransactionAddModal(invoice)"
                                >{{$t('Add payment')}}
                                </b-button>
                            </div>
                        </div>
                        <!-- / .row -->
                    </div>
                    <div class="card-body" v-if="isDataLoading">
                        <div class="row align-items-center active">
                            <div class="spinner-border text-primary" role="status">
                                <span class="sr-only">{{$t('Loading')}}...</span>
                            </div>
                        </div>
                    </div>

                    <div class="card-body" v-if="transactions.length !== 0">
                        <ul class="list-group list-group-sm list-group-flush list my-n4">
                            <li class="list-group-item px-0">
                                <div class="row">
                                    <div
                                        :class="item.value"
                                        v-for="(item, index) in titleData"
                                        :key="index"
                                    >{{ $t(item.label) }}
                                    </div>
                                    <div style="width: 10%"></div>
                                </div>
                            </li>
                            <li class="list-group-item px-0" v-for="(transaction, index) in transactions" :key="index">
                                <div class="row">
                                    <div class="col-4 col">
                                        {{ transaction.createdAt | moment("calendar") }}
                                    </div>

                                    <div class="col text-center">
                                        <div v-if="transaction.cancelled">
                                            <b-badge pill variant="danger">{{$t('Cancelled')}}</b-badge>
                                        </div>
                                        <div v-else-if="transaction.amount < 0">
                                            <b-badge pill variant="soft-danger">{{ $t('payment.status.debiting-from-balance') }}</b-badge>
                                        </div>
                                        <div v-else>
                                            <b-badge pill variant="success">{{$t('Paid')}}</b-badge>
                                        </div>
                                    </div>

                                    <div class="col text-center">
                                        <h3 v-if="transaction.paymentMethod">
                                            <b-badge
                                                pill
                                                :style="{ background: paymentMethodLabel(transaction.paymentMethod).color }"
                                            >{{$t(paymentMethodLabel(transaction.paymentMethod).label)}}
                                            </b-badge>
                                        </h3>
                                        <div v-if="transaction.payments">
                                            <b-badge
                                                pill
                                                v-for="p in transaction.payments"
                                                :key="p._id"
                                                :style="{ background: paymentMethodLabel(p.paymentMethod).color }"
                                            >
                                                {{$t(paymentMethodLabel(p.paymentMethod).label)}} -
                                                <money :value="p.toPay"/>
                                            </b-badge>
                                        </div>
                                    </div>

                                    <div class="col text-right" :class="transaction.amount > 0 ? 'income' : 'outcome'">
                                        <span v-if="transaction.amount > 0">+</span>
                                        <money :value="transaction.amount"/>
                                    </div>

                                    <div style="width: 10%;" class="d-flex justify-content-around text-right">
                                        <button
                                            class="btn btn-white"
                                            v-if="!transaction.cancelled"
                                            @click="openCancelModal(transaction)"
                                        >
                                            <i class="fe fe-x-circle pointer"></i>
                                        </button>
                                    </div>
                                </div>
                                <div class="row ma-2 p-2" v-if="transaction.cancelExplanation">
                                    <i class="text-danger fe fe-message-square mx-1"></i>
                                    <div class="col-11 text-muted">
                                        <span class="mx-1 my-3">{{ transaction.cancelExplanation }}</span>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <transactions-cancel-modal id="transactionCancelModal"></transactions-cancel-modal>
        <transactions-modal id="transactionAddModal" :parent="parent"></transactions-modal>

        <template slot="modal-footer">
            <div class="card-footer" style="display:block">
                <div class="row">
                    <div class="col">
                        <button
                            class="btn btn-white text-danger"
                            v-if="!parent.cancelled"
                            @click="openCancelModal(parent)"
                        >
                            <i class="fe fe-x-circle pointer"></i>
                        </button>
                        <button
                            class="btn btn-white ml-2"
                            v-if="parent.invoiceId"
                            @click="invoiceModal(parent.invoiceId, false)"
                        >
                            <i class="fe fe-file-text pointer"></i>
                        </button>
                    </div>
                    <div class="col-auto">
                        <button type="button" @click="close" class="btn btn-white">{{$t('Close')}}</button>
                    </div>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from "@/components/Modal.vue";
import {mapActions, mapGetters, mapState} from 'vuex';
import {modalMixins} from '@/mixins/modal.js'
import Money from '@/components/Money.vue';
import paymentMethod from '@/filters/payment.js';
import InvoiceModal from '@/Pages/Sales/InvoiceModal.vue';
import TransactionsCancelModal from "./TransactionsCancelModal";
import TransactionsModal from "./TransactionsModal";

export default {
    name: 'TransactionDetailsModal',
    props: {id: String, parent: null},
    mixins: [modalMixins],
    components: {
        Modal,
        Money,
        InvoiceModal,
        TransactionsModal,
        TransactionsCancelModal,
    },

    data() {
        return {
            titleData: [
                {value: 'col-4', label: 'Date'},
                {value: 'col text-center', label: 'Status'},
                {value: 'col text-center', label: 'Payment method'},
                {value: 'col text-center', label: 'Amount'},
            ],
            loading: false,
            hideForm: false,
            data: {},
            params: {query: {}},
            customer: {},
            user: {},
            // invoice: {}
        }
    },

    computed: {
        ...mapState('transactions', {isDataLoading: 'isFindPending'}),
        ...mapGetters('transactions', {findDataInStore: 'find'}),
        ...mapGetters('invoices', {getInvoice: 'get'}),

        invoice() {
            return this.parent.invoiceId ? this.getInvoice(this.parent.invoiceId) : null;
        },

        transactions() {
            let params = {
                query: {parentId: this.parent._id}
            };
            let data = this.findDataInStore(params).data;
            if (!this.parent.invoiceId || this.parent.status === 'topup') {
                data = data.concat(this.parent)
            }
            return data;
        },

        byDateLabel() {
            if (this.date_range == 'custom') {
                return this.date;
            } else
                return this.labels[this.date_range];
        },
        title() {
            if (!this.parent.invoiceId) return this.$t('Balance top up');
            if (this.invoice) {
                const date = this.$moment(this.invoice.createdAt).format('DD MMMM, YYYY');
                return this.$t('Invoice #') + this.invoice.invoiceNumber + ' ' + this.$t('from') + ' ' + date;
            }

        }
    },

    methods: {
        ...mapActions('transactions', {fetchTransactions: 'find'}),
        ...mapActions('invoices', {fetchInvoice: 'get'}),
        ...mapActions('users', {fetchUser: 'get'}),

        close() {
            this.$root.$emit('bv::hide::modal', this.id);
        },
        paymentMethodLabel(method) {
            return paymentMethod(method);
        },

        openCancelModal(transaction) {
            let item = {
                model: transaction,
                _id: 'temp'
            };
            const {Transaction} = this.$FeathersVuex;
            new Transaction(item);
            this.openModal({id: 'transactionCancelModal'});
        },

        shown() {
            let params = {
                query: {parentId: this.parent._id}
            };
            this.fetchTransactions(params);
            this.fetchInvoice(this.parent.invoiceId);

            this.fetchUser(this.parent.userId).then(data => {
                this.user = data
            })
        },

        openTransactionAddModal(invoice) {
            let transaction = {
                invoice,
                _id: 'temp'
            };
            const { Transaction } = this.$FeathersVuex;
            new Transaction(transaction);
            this.openModal({ id: 'transactionAddModal', modal: invoice })
        }

    },

}
</script>
<style>
</style>
